import Link from "next/link"
import Image from "next/image"
import { ArrowRight } from "lucide-react"
import { Badge } from "@/components/ui/badge"

interface ListingCardProps {
  href?: string
  title: string
  description: string
  category?: string
  meta?: string
  image?: string
  imageSlot?: React.ReactNode
  logo?: string
  iconEmoji?: string
  iconBgColor?: string
  onClick?: () => void
}

export function ListingCard({
  href,
  title,
  description,
  category,
  meta,
  image,
  imageSlot,
  logo,
  iconEmoji,
  iconBgColor,
  onClick,
}: ListingCardProps) {
  const hasImage = image || imageSlot
  const content = (
    <>
      {imageSlot && (
        <div className="relative mb-4 aspect-[16/9] w-full overflow-hidden rounded-lg">
          {imageSlot}
        </div>
      )}
      {!imageSlot && image && (
        <div className="relative mb-4 aspect-[16/9] w-full overflow-hidden rounded-lg">
          <Image
            src={image}
            alt={title}
            fill
            className="object-cover transition-transform group-hover:scale-105"
            sizes="(max-width: 768px) 100vw, 50vw"
          />
        </div>
      )}
      {!hasImage && logo && (
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg border bg-white p-2">
          <Image
            src={logo}
            alt={`${title} logo`}
            width={32}
            height={32}
            className="object-contain"
          />
        </div>
      )}
      {!hasImage && !logo && iconEmoji && (
        <div
          className={`mb-4 flex h-12 w-12 items-center justify-center rounded-xl text-2xl ${iconBgColor || "bg-muted"}`}
        >
          {iconEmoji}
        </div>
      )}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          {category && (
            <Badge variant="secondary" className="mb-3">
              {category}
            </Badge>
          )}
          <h3 className="text-lg font-semibold group-hover:text-primary">
            {title}
          </h3>
          <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
            {description}
          </p>
        </div>
        {href && !hasImage && (
          <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
        )}
      </div>
      {meta && (
        <p className="mt-4 text-xs text-muted-foreground">{meta}</p>
      )}
    </>
  )

  if (href) {
    return (
      <Link
        href={href}
        onClick={onClick}
        className="group flex flex-col overflow-hidden rounded-xl border p-6 transition-colors hover:border-primary/50 hover:bg-muted/50"
      >
        {content}
      </Link>
    )
  }

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border p-6">
      {content}
    </div>
  )
}
